import { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DomainIcon from '@mui/icons-material/Domain';
import LogoutIcon from '@mui/icons-material/Logout';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuestionnaire } from '../features/questionnaire-builder/hooks/useQuestionnaire';
import {
  useQuestionnairesList,
  useTenants,
} from '../features/questionnaire-builder/hooks/useQuestionnaires';
import { NewQuestionnaireDialog } from '../features/questionnaire-builder/components/NewQuestionnaireDialog';
import { getTenantId, setTenantId } from '../platform/apiClient';
import { isAuthEnabled, logout } from '../platform/auth/userManager';
import { BACKEND_FEATURES } from '../platform/backendFeatures';

const TAB_ROUTES = [
  { label: 'BUILDER', path: '/builder' },
  { label: 'PREVIEW', path: '/preview' },
];

export const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { slug: paramSlug } = useParams<{ slug?: string }>();

  // Tenant picker — the most-up selector. Choice is persisted to
  // localStorage by setTenantId and read by apiClient on every request.
  // Switching tenants nukes the React Query cache so stale per-tenant
  // data doesn't bleed across.
  const tenants = useTenants();
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(getTenantId());
  const [tenantAnchor, setTenantAnchor] = useState<null | HTMLElement>(null);

  // On first load with no stored tenant, default to the first one the
  // API returns so the rest of the app has something to scope to.
  useEffect(() => {
    if (!currentTenantId && tenants.data?.[0]) {
      const first = tenants.data[0].id;
      setTenantId(first);
      setCurrentTenantId(first);
    }
  }, [tenants.data, currentTenantId]);

  const switchTenant = (id: string) => {
    setTenantAnchor(null);
    if (id === currentTenantId) return;
    setTenantId(id);
    setCurrentTenantId(id);
    // Drop all per-tenant caches and go back to /builder so the redirect
    // picks the new tenant's first questionnaire.
    queryClient.removeQueries({ queryKey: ['questionnaire'] });
    queryClient.removeQueries({ queryKey: ['questionnaires'] });
    queryClient.removeQueries({ queryKey: ['questionnaire-assignment'] });
    queryClient.removeQueries({ queryKey: ['court-types'] });
    navigate('/builder');
  };

  const currentTenant = tenants.data?.find((t) => t.id === currentTenantId);

  // The picker can target either /builder/:slug or /preview/:slug — pick
  // whichever path the user is on so switching keeps them in the same tab.
  const currentTab =
    location.pathname.startsWith('/preview') ? '/preview' : '/builder';
  const activeIndex = TAB_ROUTES.findIndex((tab) =>
    location.pathname.startsWith(tab.path),
  );
  const tabValue: number | false = activeIndex === -1 ? false : activeIndex;

  const [includeDisabled, setIncludeDisabled] = useState(false);
  const list = useQuestionnairesList(includeDisabled);
  const [pickerAnchor, setPickerAnchor] = useState<null | HTMLElement>(null);
  const [newOpen, setNewOpen] = useState(false);

  const selectedSlug = paramSlug ?? list.data?.[0]?.slug;
  const { data: selected } = useQuestionnaire(selectedSlug);
  const jurisdiction = selected?.jurisdiction;
  const term = selected?.term;

  const selectedSummary = list.data?.find((q) => q.slug === selectedSlug);

  return (
    <AppBar position="static" color="primary" elevation={0} component="header">
      <Toolbar>
        <GavelIcon aria-hidden sx={{ mr: 1.5 }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" component="h1" sx={{ lineHeight: 1.1 }}>
            Juror Questionnaire Portal
          </Typography>
          {jurisdiction && term && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {jurisdiction} · {term}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
          {BACKEND_FEATURES.tenants && (
            <>
              <Button
                color="inherit"
                variant="outlined"
                startIcon={<DomainIcon />}
                endIcon={<ExpandMoreIcon />}
                onClick={(e) => setTenantAnchor(e.currentTarget)}
                sx={{ borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
                aria-label="Switch tenant"
              >
                {currentTenant?.name ?? 'Tenant…'}
              </Button>
              <Menu
                anchorEl={tenantAnchor}
                open={!!tenantAnchor}
                onClose={() => setTenantAnchor(null)}
                slotProps={{ paper: { sx: { minWidth: 320 } } }}
              >
                {tenants.data?.map((t) => (
                  <MenuItem
                    key={t.id}
                    selected={t.id === currentTenantId}
                    onClick={() => switchTenant(t.id)}
                  >
                    <ListItemText
                      primary={t.name}
                      secondary={`${t.jurisdiction} · ${t.state} · ${t.code}`}
                    />
                  </MenuItem>
                ))}
                {tenants.data && tenants.data.length === 0 && (
                  <MenuItem disabled>No tenants</MenuItem>
                )}
              </Menu>
            </>
          )}
          <Button
            color="inherit"
            variant="outlined"
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setPickerAnchor(e.currentTarget)}
            sx={{ borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <span>
                {selectedSummary?.title ?? selected?.title ?? 'Pick questionnaire'}
              </span>
              {selectedSummary && !selectedSummary.isActive && (
                <Chip
                  size="small"
                  label="Disabled"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
                />
              )}
            </Stack>
          </Button>
          <Menu
            anchorEl={pickerAnchor}
            open={!!pickerAnchor}
            onClose={() => setPickerAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 320 } } }}
          >
            <Box sx={{ px: 2, py: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeDisabled}
                    onChange={(e) => setIncludeDisabled(e.target.checked)}
                  />
                }
                label="Include disabled"
              />
            </Box>
            {list.data?.map((q) => (
              <MenuItem
                key={q.slug}
                selected={q.slug === selectedSlug}
                onClick={() => {
                  setPickerAnchor(null);
                  navigate(`${currentTab}/${q.slug}`);
                }}
              >
                <ListItemText
                  primary={q.title}
                  secondary={`${q.jurisdiction} · ${q.term}${q.isActive ? '' : ' · disabled'}`}
                />
              </MenuItem>
            ))}
            {list.data && list.data.length === 0 && (
              <MenuItem disabled>No questionnaires</MenuItem>
            )}
          </Menu>
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setNewOpen(true)}
            sx={{ borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
          >
            New
          </Button>
          {isAuthEnabled() && (
            <Button
              color="inherit"
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={() => void logout()}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
            >
              Sign out
            </Button>
          )}
        </Stack>

        <Tabs
          value={tabValue}
          onChange={(_, value: number) => {
            const next = TAB_ROUTES[value].path;
            navigate(selectedSlug ? `${next}/${selectedSlug}` : next);
          }}
          textColor="inherit"
          indicatorColor="secondary"
          aria-label="Primary navigation"
        >
          {TAB_ROUTES.map((tab) => (
            <Tab
              key={tab.path}
              label={tab.label}
              sx={{ minWidth: 96, maxWidth: 264, height: 48 }}
            />
          ))}
        </Tabs>
      </Toolbar>
      <NewQuestionnaireDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(s) => {
          setNewOpen(false);
          navigate(`/builder/${s}`);
        }}
      />
    </AppBar>
  );
};
